import { Box, Stack, Switch } from '@mui/material';
import { PartnerSettingValue } from '@provision/entity-types';
import { Headphones, MessageSquare, RadioTower, Receipt } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormTextField } from 'src/components/Forms';
import { SettingsSection, SettingsSectionRow } from 'src/components/SettingsSection';
import { PartnerSetting, usePartnerSettings } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { isStringBooleanTrue } from 'src/utils/strings';
import { ComposerPreferences } from './components/composer-preferences';
import { SlaRoutingSection } from './components/sla-routing-section';
import { SmsSessionPreferences } from './components/sms-session-preferences';
import { WorkflowEmergencyOverridePreferences } from './components/workflow-emergency-override';
import { useSlaTaxExemption } from './hooks/use-sla-tax-exemption';
import {
  PreferencesChange,
  SettingsPreferencesFormFields,
  SettingsPreferencesFormFieldsFormModel,
} from './types';
import { DEFAULT_SLA_TAX_EXEMPTION } from './utils';

export const PreferencesForm = ({
  handleChange,
  savingFields,
}: {
  handleChange: (data: PreferencesChange) => void | Promise<void>;
  savingFields: Set<SettingsPreferencesFormFields>;
}) => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { data: answeringServiceRecords } = usePartnerSettings({
    partnerId,
    partnerSetting: PartnerSetting.AnsweringServicesOn,
  });
  const { data: usesProvisionRecords } = usePartnerSettings({
    partnerId,
    partnerSetting: PartnerSetting.UsesProvisionForSupportOn,
  });
  const { data: signalAutoAssignmentRecords, loading: signalAutoAssignmentLoading } =
    usePartnerSettings({
      partnerId,
      partnerSetting: PartnerSetting.SignalAutoAssignmentDisabled,
    });

  const answeringServiceSetting = Array.isArray(answeringServiceRecords)
    ? answeringServiceRecords.find((r) => r.partnerId === partnerId)
    : undefined;
  const usesProvisionPartnerLevel = Array.isArray(usesProvisionRecords)
    ? usesProvisionRecords.find((r) => r.partnerId === partnerId)
    : undefined;
  const signalAutoAssignmentSetting = Array.isArray(signalAutoAssignmentRecords)
    ? signalAutoAssignmentRecords.find((r) => r.partnerId === partnerId)
    : undefined;

  const methods = useForm<SettingsPreferencesFormFieldsFormModel>({
    defaultValues: {
      [SettingsPreferencesFormFields.AnsweringServices]: true,
      [SettingsPreferencesFormFields.SignalAutoAssignment]: true,
      [SettingsPreferencesFormFields.UsesProvisionForSupport]: true,
      [SettingsPreferencesFormFields.SlaTaxExemption]: DEFAULT_SLA_TAX_EXEMPTION,
    },
  });
  const { control, setValue, getValues, watch } = methods;

  const slaTaxExemption = watch(SettingsPreferencesFormFields.SlaTaxExemption);

  const { serverValue, shouldSyncFromServer, handleExemptToggle, handleCustomerIdChange } =
    useSlaTaxExemption({
      getValues: () => getValues(SettingsPreferencesFormFields.SlaTaxExemption),
      setValue: (v) => setValue(SettingsPreferencesFormFields.SlaTaxExemption, v),
      handleChange,
    });

  useEffect(() => {
    setValue(
      SettingsPreferencesFormFields.AnsweringServices,
      !answeringServiceSetting
        ? true
        : (answeringServiceSetting?.value ?? '').toLocaleLowerCase() === 'true',
    );
  }, [answeringServiceSetting, setValue]);

  useEffect(() => {
    setValue(
      SettingsPreferencesFormFields.UsesProvisionForSupport,
      !usesProvisionPartnerLevel
        ? true
        : (usesProvisionPartnerLevel?.value ?? '').toLocaleLowerCase() === 'true',
    );
  }, [usesProvisionPartnerLevel, setValue]);

  useEffect(() => {
    setValue(
      SettingsPreferencesFormFields.SignalAutoAssignment,
      !isStringBooleanTrue(signalAutoAssignmentSetting?.value),
    );
  }, [signalAutoAssignmentSetting, setValue]);

  useEffect(() => {
    if (shouldSyncFromServer) {
      setValue(SettingsPreferencesFormFields.SlaTaxExemption, serverValue);
    }
  }, [serverValue, shouldSyncFromServer, setValue]);

  const handleBooleanFieldChange = useCallback(
    ({
      rule,
      id,
      value,
      storedValue = value,
      setting,
    }: {
      rule: SettingsPreferencesFormFields;
      id: string | null;
      value: boolean;
      storedValue?: boolean;
      setting: Partial<PartnerSettingValue>;
    }) => {
      const prevValue = getValues(rule);
      setValue(rule, value);
      if (prevValue !== value) {
        void handleChange({
          rule,
          value: storedValue,
          id,
          partnerId: setting?.partnerId ?? null,
        });
      }
    },
    [getValues, setValue, handleChange],
  );

  return (
    <FormProvider {...methods}>
      <Stack spacing={4}>
        <SettingsSection
          title="Platform"
          description="Platform enablement and SLA tax handling for this partner."
        >
          <SettingsSectionRow
            icon={<MessageSquare />}
            title="ProVision Conversations"
            secondary="The partner is live with ProVision conversations."
            control={
              <Controller
                name={SettingsPreferencesFormFields.UsesProvisionForSupport}
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    checked={field.value}
                    disabled={savingFields.has(
                      SettingsPreferencesFormFields.UsesProvisionForSupport,
                    )}
                    onChange={(e) =>
                      handleBooleanFieldChange({
                        rule: SettingsPreferencesFormFields.UsesProvisionForSupport,
                        id: usesProvisionPartnerLevel?.partnerSettingValueId ?? null,
                        value: e.target.checked,
                        setting: usesProvisionPartnerLevel ?? {},
                      })
                    }
                  />
                )}
              />
            }
          />
          <Box>
            <SettingsSectionRow
              icon={<Receipt />}
              title="SLA Tax Exempt"
              secondary="Enable to mark this partner's SLA products as tax-exempt. Requires a valid resale certificate in Exemptax."
              control={
                <Switch
                  checked={slaTaxExemption.exempt}
                  onChange={(e) => handleExemptToggle(e.target.checked)}
                  disabled={savingFields.has(SettingsPreferencesFormFields.SlaTaxExemption)}
                />
              }
            />
            {slaTaxExemption.exempt ? (
              <Box sx={{ pl: 8, pr: 2.25, pb: 2 }}>
                <FormTextField
                  type="textfield"
                  name={`${SettingsPreferencesFormFields.SlaTaxExemption}.exemptaxCustomerId`}
                  label="Exemptax Customer ID"
                  labelAbove
                  description="Found in your Exemptax dashboard under the customer's profile."
                  textFieldProps={{
                    placeholder: 'e.g. 12345',
                    onChange: (e) => handleCustomerIdChange(e.target.value),
                  }}
                  disabled={savingFields.has(SettingsPreferencesFormFields.SlaTaxExemption)}
                  sx={{ maxWidth: 300 }}
                />
              </Box>
            ) : null}
          </Box>
        </SettingsSection>

        <SettingsSection
          title="Routing"
          description="How conversations are answered, assigned, and routed."
        >
          <SettingsSectionRow
            icon={<Headphones />}
            title="Answering Services"
            secondary="Basic Support manages incoming requests from non-members, providing triage to de-escalate concerns, gather key details, and set clear expectations. Requests are documented and routed efficiently for further support or membership opportunities."
            control={
              <Controller
                name={SettingsPreferencesFormFields.AnsweringServices}
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    checked={field.value}
                    disabled={savingFields.has(
                      SettingsPreferencesFormFields.AnsweringServices,
                    )}
                    onChange={(e) =>
                      handleBooleanFieldChange({
                        rule: SettingsPreferencesFormFields.AnsweringServices,
                        id: answeringServiceSetting?.partnerSettingValueId ?? null,
                        value: e.target.checked,
                        setting: answeringServiceSetting ?? {},
                      })
                    }
                  />
                )}
              />
            }
          />
          {!signalAutoAssignmentLoading ? (
            <SettingsSectionRow
              icon={<RadioTower />}
              title="Signal auto-assignment"
              secondary="Automatically assign conversations to the on-call agents Signal notifies."
              control={
                <Controller
                  name={SettingsPreferencesFormFields.SignalAutoAssignment}
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      checked={field.value}
                      disabled={savingFields.has(
                        SettingsPreferencesFormFields.SignalAutoAssignment,
                      )}
                      onChange={(event) =>
                        handleBooleanFieldChange({
                          rule: SettingsPreferencesFormFields.SignalAutoAssignment,
                          id: signalAutoAssignmentSetting?.partnerSettingValueId ?? null,
                          value: event.target.checked,
                          storedValue: !event.target.checked,
                          setting: signalAutoAssignmentSetting ?? {},
                        })
                      }
                      slotProps={{ input: { 'aria-label': 'Enable Signal auto-assignment' } }}
                    />
                  )}
                />
              }
            />
          ) : null}
          <SlaRoutingSection partnerId={partnerId} />
        </SettingsSection>

        <SmsSessionPreferences />

        <WorkflowEmergencyOverridePreferences />

        <ComposerPreferences />
      </Stack>
    </FormProvider>
  );
};
